import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { authApi } from "../../api/authApi";
import {
  changePassword,
  logout,
  resetAuthStatuses,
} from "../../store/slices/authSlice";

const MIN_LENGTH = 6;

/** Returns what is wrong with the form, or "" when it can be sent. */
export const passwordFormError = ({ current, next, confirm }) => {
  if (!current || !next || !confirm) return "All fields are required.";
  if (next.length < MIN_LENGTH)
    return `New password must be at least ${MIN_LENGTH} characters long.`;
  if (next === current)
    return "New password must be different from the temporary one.";
  if (next !== confirm) return "Passwords do not match.";
  return "";
};

/**
 * First login with a temporary password: AppRoutes keeps the user here until
 * the change succeeds. The only way out is signing out.
 */
const SetPasswordPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { passwordStatus, error } = useSelector((state) => state.auth);
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [localError, setLocalError] = useState("");
  const busy = passwordStatus === "loading";
  const message = localError || error;

  useEffect(() => {
    dispatch(resetAuthStatuses());
  }, [dispatch]);

  const field = (key) => ({
    value: form[key],
    onChange: (e) => setForm((prev) => ({ ...prev, [key]: e.target.value })),
    disabled: busy,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(resetAuthStatuses());
    const problem = passwordFormError(form);
    setLocalError(problem);
    if (problem) return;

    const result = await dispatch(
      changePassword({ oldPassword: form.current, newPassword: form.next }),
    );
    if (changePassword.fulfilled.match(result)) {
      toast.success("Password changed.");
      navigate("/", { replace: true });
    }
  };

  const signOut = async () => {
    try {
      await authApi.logout();
    } catch {
      /* token already invalid or offline */
    }
    dispatch(logout());
    queryClient.clear();
    navigate("/login", { replace: true });
  };

  return (
    <div className="auth-page">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-sm-10 col-md-8 col-lg-5 col-xl-4">
            <div className="glass-card shadow-lg p-4 p-sm-5">
              <div className="text-center mb-4">
                <h2 className="fw-bolder m-0 auth-brand-title fs-140">
                  Change your password
                </h2>
                <p className="text-muted small mt-2 fw-medium">
                  You signed in with a temporary password. Choose your own to
                  continue.
                </p>
              </div>

              {message && (
                <div
                  className="alert alert-danger py-2 border-0 small mb-4 d-flex align-items-center rounded-3"
                  role="alert"
                >
                  <i className="bi bi-exclamation-circle-fill me-2"></i>
                  {message}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label
                    htmlFor="set-current"
                    className="form-label fw-bold small text-secondary"
                  >
                    Temporary password
                  </label>
                  <input
                    id="set-current"
                    type="password"
                    className="form-control form-control-lg login-input fs-090 rounded-10"
                    autoComplete="current-password"
                    autoFocus
                    required
                    {...field("current")}
                  />
                </div>
                <div className="mb-3">
                  <label
                    htmlFor="set-new"
                    className="form-label fw-bold small text-secondary"
                  >
                    New password
                  </label>
                  <input
                    id="set-new"
                    type="password"
                    className="form-control form-control-lg login-input fs-090 rounded-10"
                    autoComplete="new-password"
                    minLength={MIN_LENGTH}
                    required
                    {...field("next")}
                  />
                  <div className="form-text">
                    At least {MIN_LENGTH} characters.
                  </div>
                </div>
                <div className="mb-4">
                  <label
                    htmlFor="set-confirm"
                    className="form-label fw-bold small text-secondary"
                  >
                    Confirm new password
                  </label>
                  <input
                    id="set-confirm"
                    type="password"
                    className="form-control form-control-lg login-input fs-090 rounded-10"
                    autoComplete="new-password"
                    required
                    {...field("confirm")}
                  />
                </div>
                <div className="d-grid gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg fw-bold shadow-sm border-0 auth-submit"
                    disabled={busy}
                  >
                    {busy ? "Saving…" : "Change password"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-link text-decoration-none fw-bold auth-link"
                    onClick={signOut}
                    disabled={busy}
                  >
                    Sign out
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetPasswordPage;
